/**
 * StorageManager - Handles saving and loading game data using localStorage
 */
export interface GameSettings {
  soundEffectsEnabled: boolean;
  musicEnabled: boolean;
}

export interface LeaderboardEntry {
  playerName: string;
  score: number;
  role: 'runner' | 'chaser';
  date: number; // timestamp
}

export class StorageManager {
  private static instance: StorageManager;
  
  // Storage keys
  private readonly SETTINGS_KEY = 'metrotag_settings';
  private readonly LEADERBOARD_KEY = 'metrotag_leaderboard';
  
  // Private constructor for singleton
  private constructor() {}
  
  // Get singleton instance
  public static getInstance(): StorageManager {
    if (!StorageManager.instance) {
      StorageManager.instance = new StorageManager();
    }
    return StorageManager.instance;
  }
  
  /**
   * Save game settings to localStorage
   * @param settings The settings object to save
   */
  public saveSettings(settings: GameSettings): void {
    try {
      localStorage.setItem(this.SETTINGS_KEY, JSON.stringify(settings));
    } catch (error) {
      console.error('Failed to save settings to localStorage:', error);
    }
  }
  
  /**
   * Load game settings from localStorage
   * @returns The saved settings or default settings if none found
   */
  public loadSettings(): GameSettings {
    try {
      const savedSettings = localStorage.getItem(this.SETTINGS_KEY);
      if (savedSettings) {
        return JSON.parse(savedSettings) as GameSettings;
      }
    } catch (error) {
      console.error('Failed to load settings from localStorage:', error);
    }
    
    // Return default settings if none found or error occurred
    return {
      soundEffectsEnabled: true,
      musicEnabled: true
    };
  }
  
  /**
   * Save a leaderboard entry
   * @param entry The leaderboard entry to save
   */
  public saveLeaderboardEntry(entry: LeaderboardEntry): void {
    try {
      const leaderboard = this.getLeaderboard();
      leaderboard.push(entry);
      
      // Sort leaderboard by score (highest first)
      leaderboard.sort((a, b) => b.score - a.score);
      
      // Limit to top 100 entries to prevent storage issues
      const trimmedLeaderboard = leaderboard.slice(0, 100);
      
      localStorage.setItem(this.LEADERBOARD_KEY, JSON.stringify(trimmedLeaderboard));
    } catch (error) {
      console.error('Failed to save leaderboard entry:', error);
    }
  }
  
  /**
   * Get the current leaderboard
   * @returns Array of leaderboard entries
   */
  public getLeaderboard(): LeaderboardEntry[] {
    try {
      const savedLeaderboard = localStorage.getItem(this.LEADERBOARD_KEY);
      if (savedLeaderboard) {
        return JSON.parse(savedLeaderboard) as LeaderboardEntry[];
      }
    } catch (error) {
      console.error('Failed to load leaderboard from localStorage:', error);
    }
    
    // Return empty array if none found or error occurred
    return [];
  }
  
  /**
   * Clear the leaderboard
   */
  public clearLeaderboard(): void {
    try {
      localStorage.removeItem(this.LEADERBOARD_KEY);
    } catch (error) {
      console.error('Failed to clear leaderboard:', error);
    }
  }
  
  /**
   * Filter leaderboard by role
   * @param role The role to filter by ('runner' or 'chaser')
   * @returns Filtered leaderboard entries
   */
  public getLeaderboardByRole(role: 'runner' | 'chaser'): LeaderboardEntry[] {
    const leaderboard = this.getLeaderboard();
    return leaderboard.filter(entry => entry.role === role);
  }
  
  /**
   * Get the top N entries from the leaderboard
   * @param count Number of entries to return
   * @returns Top leaderboard entries
   */
  public getTopScores(count: number = 10): LeaderboardEntry[] {
    const leaderboard = this.getLeaderboard();
    return leaderboard.slice(0, count);
  }
} 